import { MeilisearchSearchResponse } from "../types/api";
import { SearchResults } from "./types/SearchResults";
import { productNormalizer } from "./normalizers/productNormalizer";
import { categorySource } from "./categories/categorySource";
import { categoryMatcher } from "./categories/categoryMatcher";
import { productVariantsGrouper } from "./groupers/productVariantsGrouper";
import { isBitrixMode } from "../utils/bitrixApiUrl";

/**
 * Map raw Meilisearch response to normalized SearchResults
 *
 * Rules:
 * 1. Pure function - no side effects
 * 2. No UI logic, no ranking, no business logic
 * 3. Each hit processed through appropriate normalizer
 * 4. Invalid hits filtered out (null results)
 * 5. Preserve total count and raw response reference
 *
 * @param rawResponse Raw response from Meilisearch (via proxy)
 * @param query Search query string (optional, defaults to rawResponse.query)
 * @returns Normalized SearchResults for UI consumption
 */
export function searchMapper(
  rawResponse: MeilisearchSearchResponse,
  query?: string
): SearchResults {
  // Use explicit query parameter or fall back to rawResponse.query
  const searchQuery = query ?? rawResponse.query;

  // Split category hits (backend Bitrix fallback emits hits with type: "category")
  const rawHits = Array.isArray(rawResponse.hits) ? rawResponse.hits : [];
  const categoryHits = rawHits.filter(
    (hit) =>
      hit &&
      typeof hit === "object" &&
      (hit as Record<string, unknown>).type === "category"
  );
  const productHits = rawHits.filter((hit) => !categoryHits.includes(hit));

  // Process each hit through product normalizer
  const normalizedProducts = productHits
    .map((hit) => productNormalizer(hit))
    .filter((product): product is NonNullable<typeof product> => product !== null);

  // Group product variants by "Основное наименование"
  const products = productVariantsGrouper(normalizedProducts);

  // Categories from backend hits (already normalized server-side)
  const engineCategories: any[] = categoryHits
    .map((hit) => {
      const h = hit as Record<string, unknown>;
      if (typeof h.id !== "string" && typeof h.id !== "number") return null;
      if (typeof h.title !== "string" || !h.title.trim()) return null;
      return {
        id: h.id,
        type: "category" as const,
        title: h.title,
        parentId: typeof h.parentId === "string" || typeof h.parentId === "number" ? h.parentId : undefined,
        level: typeof h.level === "number" ? h.level : undefined,
        url: typeof h.url === "string" && h.url ? h.url : `/category/${h.id}`,
        image: typeof h.image === "string" && h.image ? h.image : undefined,
        raw: hit,
      };
    })
    .filter((c): c is NonNullable<typeof c> => c !== null);

  // Match categories (dev-only feed-based implementation)
  // В Bitrix-режиме фид /feed/categories.json не используется: категории
  // приходят только из бэкенда (хиты с type: "category", с картинками).
  let categories: any[] = engineCategories;
  try {
    const allCategories = isBitrixMode() ? [] : categorySource.getCategoriesSync();

    if (Array.isArray(allCategories) && allCategories.length > 0) {
      const feedCategories = categoryMatcher.match(allCategories, searchQuery);
      // Merge: engine categories first, dedupe by id
      const seen = new Set(engineCategories.map((c) => String(c.id)));
      for (const cat of feedCategories) {
        if (!seen.has(String(cat.id))) {
          categories.push(cat);
          seen.add(String(cat.id));
        }
      }
    }
    // If categories not loaded yet or empty, engine categories (or empty) remains
  } catch (error) {
    // Fallback for unexpected errors (dev environment only)
    console.error("[searchMapper] Unexpected error matching categories:", error);
    categories = engineCategories;
  }

  // Return normalized results
  return {
    products,
    categories, // Real categories from feed (dev) or empty array if not loaded
    suggestions: [], // Placeholder for future suggestion normalization
    total: rawResponse.estimatedTotalHits,
    raw: rawResponse, // Optional reference to raw response
  };
}
